import React from 'react';
import { useDispatch } from 'react-redux';
import { Tour, TourProps, theme } from 'antd';

import { useTrial } from '../useTrial';
import { useAvailableTargets, registerComponent, unregisterComponent } from '../ReduxSlice';

export type WalkthroughTargetId = 'cache-settings' | 'render-counter';

export function useWalkthroughTargets() {
  const availableTargets = useAvailableTargets();
  const dispatch = useDispatch();

  const updateAvailableTargets = (targetId: WalkthroughTargetId, isVisible = false) => {
    if (isVisible) {
      dispatch(registerComponent(targetId));
    } else {
      dispatch(unregisterComponent(targetId));
    }
  };

  return {
    availableTargets,
    updateAvailableTargets,
  };
}

function Walkthrough() {
  const { availableTargets } = useWalkthroughTargets();
  const { updateFreeTrialState, freeTrialState, isInTrial } = useTrial();
  const { token } = theme.useToken();

  const open =
    isInTrial &&
    freeTrialState.user.trialStartedModalClosed &&
    (freeTrialState.user.trialWalkthrough.displayRenderCounter ||
      freeTrialState.user.trialWalkthrough.displayCacheFreshness);

  const onCloseTourStep = (step: string) => () => {
    if (step === 'render-counter') {
      updateFreeTrialState({
        state: {
          trialWalkthrough: {
            ...freeTrialState.user.trialWalkthrough,
            displayRenderCounter: false,
            renderCounterViewed: true,
          },
        },
        scope: 'user',
      });
    } else if (step === 'cache-settings') {
      updateFreeTrialState({
        state: {
          trialWalkthrough: {
            ...freeTrialState.user.trialWalkthrough,
            displayCacheFreshness: false,
            cacheFreshnessViewed: true,
          },
        },
        scope: 'user',
      });
    }
  };

  const steps: TourProps['steps'] = React.useMemo(() => {
    const renderCounterTarget = availableTargets['render-counter']
      ? (document.querySelector('#render-counter') as HTMLDivElement)
      : null;
    const cacheSettingsTarget = availableTargets['cache-settings']
      ? (document.querySelector('#cache-settings') as HTMLDivElement)
      : null;

    const tourSteps: TourProps['steps'] = [];

    if (freeTrialState.user.trialWalkthrough.displayRenderCounter && renderCounterTarget)
      tourSteps.push({
        title: 'You now have more renders!',
        description: 'Check your render usage here.',
        target: renderCounterTarget,
        style: {
          maxWidth: 280,
          boxShadow: token.boxShadow,
        },
        nextButtonProps: {
          children: 'Got it',
          style: {
            padding: 15,
          },
          onClick: onCloseTourStep('render-counter'),
        },
        onClose: onCloseTourStep('render-counter'),
      });

    if (freeTrialState.user.trialWalkthrough.displayCacheFreshness && cacheSettingsTarget)
      tourSteps.push({
        title: 'Avoid stale content with more caching',
        description:
          'Frequent caching helps search engines keep up with your quick-changing pages. Say goodbye to outdated content in SERPs.',
        target: cacheSettingsTarget,
        style: {
          maxWidth: 280,
          boxShadow: token.boxShadow,
        },
        nextButtonProps: {
          children: 'Got it',
          style: {
            padding: 15,
          },
          onClick: onCloseTourStep('cache-settings'),
        },
        onClose: onCloseTourStep('cache-settings'),
      });

    return tourSteps;
  }, [
    freeTrialState.user.trialWalkthrough.displayRenderCounter,
    freeTrialState.user.trialWalkthrough.displayCacheFreshness,
    availableTargets['cache-settings'],
    availableTargets['render-counter'],
  ]);

  return <Tour open={open} steps={steps} current={0} placement="bottomRight" disabledInteraction />;
}

export default Walkthrough;
